'use client'

import { tw } from 'utils/classnames'
import Image from 'next/image'
import { useProject } from '../_hooks/use-project-data'
import { useParams } from 'next/navigation'
import { NEXT_PUBLIC_CF_URL } from '@web/utils/env'
import { getProductUrl, prodCategoriesMap } from '../utils/product-url'
import { Link } from '@repo/web-ui/components/link'
import { useHasAllAccessQuery } from '@repo/cf-user/hooks/use-user-data'
import dynamic from 'next/dynamic'

const ClientOnlyImageAds = dynamic(() => import('./right-sidebar/image-ads'), {
  ssr: false,
})

// oxlint-disable-next-line complexity
export function SidebarMasterfeedDetails({ className }: { className?: string }) {
  const slug = useParams<{ slug: string }>().slug
  const { project } = useProject({
    postSlug: slug || '',
    enabled: true,
  })
  const { data: userHasAllAccess } = useHasAllAccessQuery()

  const isShowAd = !userHasAllAccess

  function getCategoryUrl(category: string) {
    const matchingCategory = prodCategoriesMap.get(category)
    return matchingCategory
      ? `${NEXT_PUBLIC_CF_URL}/${matchingCategory.slug}/`
      : `${NEXT_PUBLIC_CF_URL}/${category}/`
  }

  return (
    <section className={tw('relative hidden w-[308px] shrink-0 self-stretch lg:block', className)}>
      <div className="bg-cf-white border border-cf-transparent-grey-1000-12 rounded-[12px] p-4">
        {project?.level && project?.level !== 'unspecified' ? (
          <div className="flex flex-row items-center justify-between">
            <p className="text-14 font-bold text-cf-black">{'Skill level'}</p>
            <div
              className={tw(
                'rounded-[24px] bg-cf-grey-50 text-cf-grey-800 border border-cf-transparent-grey-1000-12 leading-4 h-6 flex items-center justify-center text-14 font-semibold px-2.5',
                project?.level === 'beginner' ? 'bg-cf-theme-green-700 text-cf-white' : null,
                project?.level === 'intermediate' ? 'bg-cf-orange-800 text-cf-white' : null,
                project?.level === 'advanced' ? 'bg-cf-blue-800 text-cf-white' : null,
              )}
            >
              {project?.level?.charAt(0).toUpperCase() + project?.level?.slice(1)}
            </div>
          </div>
        ) : null}
        {project?.themeItems ? (
          <div className="flex flex-row items-center justify-between mt-3">
            <p className="text-14 font-bold text-cf-black">{'Theme'}</p>
            <div className="rounded-[24px] bg-cf-grey-50 border h-6 flex items-center justify-center border-cf-transparent-grey-1000-12 leading-4 text-cf-black text-14 font-semibold px-2.5">
              {project?.themeItems[0]?.title ?? ''}
            </div>
          </div>
        ) : null}
        {project?.instructionItems ? (
          <div className="flex flex-row items-center justify-between mt-3">
            <p className="text-14 font-bold text-cf-black">{'Amount of steps'}</p>
            <div className="rounded-[24px] bg-cf-grey-50 border h-6 flex items-center justify-center border-cf-transparent-grey-1000-12 leading-4 text-cf-black text-14 font-semibold px-2.5">
              {project?.instructionItems?.length ?? 0}
            </div>
          </div>
        ) : null}
        {project?.machineItems ? (
          <>
            <p className="text-14 font-bold text-cf-black mt-6">{'Machines'}</p>
            {project?.machineItems?.map(machine => (
              <div
                className="border border-cf-transparent-grey-1000-12 rounded-[12px] p-3 mt-2"
                key={machine.id}
              >
                <p className="text-14 font-semibold text-cf-black">{machine.title}</p>
                <p className="text-[11px] font-normal text-cf-grey-600">{machine.type}</p>
              </div>
            ))}
          </>
        ) : null}
        {project?.materialItems ? (
          <>
            <p className="text-14 font-bold text-cf-black mt-6">{'Materials'}</p>
            {project?.materialItems?.map(material => (
              <div className="mt-3" key={material.id}>
                <p className="text-14 leading-4 text-cf-black">{material.title}</p>
              </div>
            ))}
          </>
        ) : null}
        {project?.socialProducts ? (
          <>
            <p
              className={tw(
                'text-14 font-bold text-cf-black',
                project?.level && project?.level !== 'unspecified' ? 'mt-6' : '',
              )}
            >
              {'Products'}
            </p>
            {project?.socialProducts?.map(product => (
              <Link
                className="mt-2 border hover:shadow-md flex flex-row items-center justify-start bg-cf-grey-25 border-cf-transparent-grey-1000-12 rounded-[12px] p-1"
                key={product.id}
                href={getProductUrl({ authorId: product.author.id, productSlug: product.slug })}
                target="_blank"
              >
                <Image
                  className="aspect-[3/2] rounded-2 object-cover"
                  src={product.thumbnail ?? ''}
                  alt={product.title}
                  width={84}
                  height={56}
                />
                <div className="flex-1 min-w-0 ml-2">
                  <Link
                    href={getCategoryUrl(product.category)}
                    target="_blank"
                    className="inline-flex w-fit rounded-4 text-nowrap truncate bg-cf-theme-green-50 hover:bg-cf-theme-green-200 border border-[#1313151F] focus-visible:shadow-none focus-within:ring-1 focus-within:outline-cf-blue-700 py-0.5 px-2"
                    data-testid="discussion-product-card-category-link"
                  >
                    <p className="text-cf-grey-600 font-semibold text-[10px] uppercase leading-[10px]">
                      {product.category}
                    </p>
                  </Link>
                  <p className="truncate text-14 leading-4 mt-1 text-cf-black font-semibold">
                    {product.title}
                  </p>
                </div>
              </Link>
            ))}
          </>
        ) : null}
      </div>
      {isShowAd ? (
        <div className={'flex w-[308px] flex-col gap-4 mt-4'}>
          <div className="border-[#1313151F] rounded-[12px] border p-3 bg-cf-white">
            <p className="text-cf-purple-900 text-12 inline-block pb-3 font-medium uppercase">
              {'Sponspored'}
            </p>
            <ClientOnlyImageAds />
          </div>
        </div>
      ) : null}
    </section>
  )
}
